'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';

import { FundPromotionsEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { BlockProps } from '@sola/schema/lib/schema.js';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.js';
import { useListCatalogQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { TrackError } from '@sola/tracking/components/datalayer.js';

import { CardList } from '../../../molecules/card-list/card-list';
import {
  GiftCatalogCard,
  GiftCatalogCardSkeleton,
} from '../../../molecules/gift-catalog-card/gift-catalog-card';

const queryClient = new QueryClient();

export const GiftCatalogInsertSkeleton = () => (
  <section className="GiftCatalogInsert component component--no-header">
    <div className="container">
      <CardList columns={1} columnsMd={3}>
        {Array.from({ length: 3 }, (_, index) => (
          <GiftCatalogCardSkeleton key={`gift-insert-skeleton-${index}`} />
        ))}
      </CardList>
    </div>
  </section>
);

const InternalGiftCatalogInsert: React.FC<BlockProps> = () => {
  const searchInput = useMemo(
    () => ({
      perPage: 3,
      filters: JSON.stringify({
        'taxonomy.fundPromotion.keyword': FundPromotionsEnum.enum.popular,
      }),
      sort: 'dateModified' as const,
    }),
    [],
  );

  const {
    data: searchResults,
    isLoading,
    error,
  } = useListCatalogQuery(searchInput);

  const transformedResults = useMemo(() => {
    if (!searchResults?.items) {
      return [];
    }

    return searchResults.items.map((item) => {
      const searchResultProps = extractArgsSearchResult(item);
      return {
        ...searchResultProps,
      };
    });
  }, [searchResults]);

  if (isLoading) {
    return <GiftCatalogInsertSkeleton />;
  }

  if (error) {
    return (
      <TrackError
        errorMessage={error?.message || 'Unknown Error at gift-catalog-insert'}
      />
    );
  }

  if (!transformedResults.length) {
    return null;
  }

  return (
    <section className="GiftCatalogInsert component component--no-header">
      <div className="container">
        <CardList columns={1} columnsMd={3}>
          {transformedResults.map((gift, index) => (
            <GiftCatalogCard
              key={gift.path || `gift-insert-${index}`}
              {...gift}
            />
          ))}
        </CardList>
      </div>
    </section>
  );
};

export const GiftCatalogInsert: React.FC<BlockProps> = (props) => {
  return (
    <Suspense fallback={<GiftCatalogInsertSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <InternalGiftCatalogInsert {...props} />
      </QueryClientProvider>
    </Suspense>
  );
};
